import { useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

interface BrandingConfig {
  name: string;
  primaryColor: string;
  secondaryColor: string;
  message: string;
}

interface BrandingInfo {
  businessName: string;
  logoUrl: string;
  theme?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
  message?: string;
}

interface PointerFile {
  logoUrl: string;
  configUrl: string;
}

export const fetchBrandingInfo = async (
  businessSlug: string | undefined
): Promise<BrandingInfo> => {
  if (!businessSlug) {
    return {
      businessName: "",
      logoUrl: "",
      theme: {
        primaryColor: "#0077CC",
        secondaryColor: "#FFFFFF",
      },
    };
  }
  const baseUrl = window.location.origin.includes("localhost")
    ? "https://smartwiz.link"
    : window.location.origin;

  try {
    // First fetch the pointer file
    const pointerUrl = `${baseUrl}/branding/${businessSlug}/latest.json`;
    const pointerResponse = await axios.get<PointerFile>(pointerUrl);
    const { logoUrl, configUrl } = pointerResponse.data;

    // Then fetch the actual config using the URL from the pointer
    const configResponse = await axios.get<BrandingConfig>(configUrl);
    const config = configResponse.data;

    return {
      businessName: config.name,
      logoUrl: logoUrl,
      theme: {
        primaryColor: config.primaryColor,
        secondaryColor: config.secondaryColor,
      },
      message: config.message,
    };
  } catch (err) {
    console.error("Error fetching branding info:", err);
    // Return default values if fetch fails
    return {
      businessName: "",
      logoUrl: "",
      theme: {
        primaryColor: "#0077CC",
        secondaryColor: "#FFFFFF",
      },
    };
  }
};

export const useBranding = () => {
  const { businessSlug } = useParams();

  const {
    data: businessInfo,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["branding", businessSlug],
    queryFn: () => fetchBrandingInfo(businessSlug),
    staleTime: 1000 * 60 * 60, // Data is considered fresh for 1 hour
    gcTime: 1000 * 60 * 60 * 24, // Cache is kept for 24 hours
    retry: 2, // Retry failed requests 2 times
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  return {
    businessInfo,
    loading: isLoading,
    error: error ? "Failed to fetch branding information" : null,
  };
};
